import { CommonUtility } from '@utility/common';

/*
 * The provider's inventory tab: which quantity an item shows, what the user
 * changed, and what saving sends. Ported from the legacy tab, rule for rule.
 */

export type Measure = { value?: number | string; unit?: string };

export type CartonAttribute = {
  alias?: string | null;
  units_per_carton?: number;
  carton_length?: Measure;
  carton_width?: Measure;
  carton_height?: Measure;
  carton_net_weight?: Measure;
  carton_gross_weight?: Measure;
};

export type WmsData = {
  lotNumber?: string | null;
  expiryDate?: string | null;
  isPallet?: boolean;
  priority?: number;
};

export type InventoryLot = WmsData & { available: number };

export type InventoryVariant = {
  _id: string;
  alias?: string;
  available: number;
  available_qty?: number;
  primary?: boolean;
  attribute?: CartonAttribute;
  wms_data?: WmsData;
  lots?: InventoryLot[];
  inventory_summary?: {
    damagedQuantity?: number;
    refurbishableQuantity?: number;
    inboundWorkingQuantity?: number;
    outboundQuantity?: number;
  };
  reservedQuantity?: number;
  outboundQuantity?: number;
};

export type ScmPlace = {
  _id?: string;
  name?: string;
  address?: string;
  marketplace_region_name?: string;
};

export type ScmShipment = {
  _id?: string;
  display_id?: string;
  po_display_id?: string;
  status?: string;
  qty?: number;
  ETA?: string;
  origin?: ScmPlace;
  origin_id?: string;
  origin_type?: string;
  destination?: string | ScmPlace;
};

export type InventoryItem = {
  _id: string;
  item_id: string;
  item_sku?: string;
  item_title?: string;
  upc?: string;
  state?: string;
  alias?: string;
  is_bundle?: boolean;
  is_duplicate_listing?: boolean;
  is_blank_box?: boolean;
  storage_class?: string;
  fulfillment_provider_id?: string;
  unit_of_measurement?: string;
  item_images?: { display_image?: string };
  variants: InventoryVariant[];
  attribute?: CartonAttribute;
  available?: number;
  total_qty?: number;
  units_per_carton?: number;
  inbound_qty?: number;
  days_left?: number;
  sales_velocity?: number;
  reservedQuantity?: number;
  reservedPendingCustomerOrderQuantity?: number;
  reservedWarehouseProcessingQuantity?: number;
  reservedWarehouseTransferQuantity?: number;
  inventory_by_sku?: { sku: string; available: number }[];
  scm?: {
    existing_shipments?: ScmShipment[];
    predicted_shipments?: ScmShipment[];
  };
};

/** What the provider is, as the tab's rules read it. */
export type InventoryFlags = {
  isFBA: boolean;
  isDeliverr: boolean;
  isManual: boolean;
  isWarehouse: boolean;
  isFulfillable: boolean;
};

/** Cartons per item, and per alias for an item with several. */
export type CartonQuantities = Record<
  string,
  { available: number; oldValue: number; byVariant: Record<string, number> }
>;

/** Units held under no alias, per item. */
export type LooseQuantities = Record<string, { old: number; newValue: number }>;

type LatestVariant = Partial<InventoryVariant> & {
  attribute: CartonAttribute;
  lots?: InventoryLot[];
};

const round2 = (value: number) => Math.round(value * 100) / 100;

const perCarton = (attribute?: CartonAttribute) =>
  attribute?.units_per_carton || 1;

/**
 * The variant that speaks for the item: the primary one, else the latest
 * stocked one with an alias, the latest stocked, the latest with a carton, the
 * latest with an alias, the first, and last of all the item itself.
 */
export function latestVariant(item: InventoryItem): LatestVariant {
  const variants = item.variants ?? [];
  const latestFirst = [...variants].reverse();
  const found: Partial<InventoryVariant> | InventoryItem | undefined =
    variants.length
      ? variants.find(x => x.primary && x.attribute) ||
        latestFirst.find(x => x.available && !!x.alias && x.attribute) ||
        latestFirst.find(x => x.available && x.attribute) ||
        latestFirst.find(x => x.attribute) ||
        latestFirst.find(x => x.alias) ||
        latestFirst[latestFirst.length - 1] ||
        (item.attribute ? item : undefined)
      : item;

  if (found?.attribute) {
    return found as LatestVariant;
  }

  return {
    ...(found as Partial<InventoryVariant>),
    attribute: {
      alias: item.alias || variants.find(x => x.alias)?.alias || null,
    },
  };
}

export const aliasedVariants = (item: InventoryItem) =>
  (item.variants ?? []).filter(x => !!x.alias);

/** The item is held under more than one carton alias. */
export const hasManyAliases = (item: InventoryItem) =>
  aliasedVariants(item).length > 1;

const hasUnaliasedVariant = (item: InventoryItem) =>
  (item.variants ?? []).some(x => !x.alias);

/** Aliases the item is held under; the item's own alias counts when it has none. */
export function aliasCount(item: InventoryItem, latest: CartonAttribute) {
  const count = aliasedVariants(item).length;

  return count === 0 && latest?.alias ? 1 : count;
}

/** Units across the item's aliases. */
export function aliasedUnits(item: InventoryItem) {
  if (!item.variants?.length) return item.total_qty ?? 0;

  return aliasedVariants(item).reduce((sum, x) => sum + x.available, 0);
}

/** Cartons across every variant with a carton. */
export function cartonCount(item: InventoryItem) {
  if (!item.variants?.length) {
    return item.units_per_carton
      ? (item.total_qty ?? 0) / item.units_per_carton
      : null;
  }

  return item.variants.reduce(
    (sum, x) =>
      sum +
      (x.attribute?.units_per_carton
        ? x.available / x.attribute.units_per_carton
        : 0),
    0,
  );
}

/** Cartons as the inputs start: per item, and per alias when it has several. */
export function initialCartons(
  data: InventoryItem[],
  readOnly: boolean,
): CartonQuantities {
  const result: CartonQuantities = {};

  for (const item of data) {
    const latest = latestVariant(item);
    const { attribute } = latest;
    const count = item.variants?.length ?? 0;
    const unaliased = hasUnaliasedVariant(item);
    let available = 0;

    if (
      (unaliased && count === 2) ||
      (!unaliased && count === 1) ||
      count > 2
    ) {
      if (!readOnly || attribute?.units_per_carton) {
        available = (latest.available || 0) / perCarton(attribute);
      }
    }

    const aliased = aliasedVariants(item);
    const byVariant: Record<string, number> = {};

    if (aliased.length > 1) {
      available = aliased.reduce((sum, x) => sum + x.available, 0);
      for (const variant of aliased) {
        byVariant[variant._id] = variant.attribute?.units_per_carton
          ? variant.available / variant.attribute.units_per_carton
          : 0;
      }
    }

    available = Math.max(round2(available), 0);
    result[item.item_id] = { available, oldValue: available, byVariant };
  }

  return result;
}

/**
 * Units held under no alias. Read-only providers merge lots by alias, so there
 * the units of every variant without a carton add up.
 */
export function initialLoose(
  data: InventoryItem[],
  readOnly: boolean,
): { loose: LooseQuantities; hasLoose: boolean } {
  const loose: LooseQuantities = {};
  let hasLoose = false;

  for (const item of data) {
    for (const variant of item.variants ?? []) {
      if (readOnly) {
        if (!variant.attribute?.units_per_carton && variant.available) {
          hasLoose = true;
          const previous = loose[item.item_id] ?? { old: 0, newValue: 0 };
          loose[item.item_id] = {
            old: previous.old + variant.available,
            newValue: previous.newValue + variant.available,
          };
        }
      } else if (!variant.alias && variant.available) {
        hasLoose = true;
        loose[item.item_id] = {
          old: variant.available,
          newValue: variant.available,
        };
      }
    }
    loose[item.item_id] ??= { old: 0, newValue: 0 };
  }

  return { loose, hasLoose };
}

/** Items whose cartons or loose units differ from where they started. */
export function changedItems(
  cartons: CartonQuantities,
  initial: CartonQuantities,
  loose: LooseQuantities,
): Set<string> {
  const changed = new Set<string>();

  for (const [id, entry] of Object.entries(cartons)) {
    if (Number(entry.available || 0) !== Number(entry.oldValue || 0)) {
      changed.add(id);
    }
    for (const [variantId, value] of Object.entries(entry.byVariant)) {
      if (Number(value) !== Number(initial[id]?.byVariant[variantId])) {
        changed.add(id);
      }
    }
  }

  for (const [id, entry] of Object.entries(loose)) {
    if (Number(entry.newValue || 0) !== Number(entry.old || 0)) {
      changed.add(id);
    }
  }

  return changed;
}

type SaveVariant = { available: number; alias?: string };

/** The inventory update for every listed item, cartons sent as units. */
export function buildInventoryUpdate(
  data: InventoryItem[],
  cartons: CartonQuantities,
  loose: LooseQuantities,
  { isManual, isFulfillable, isWarehouse }: InventoryFlags,
) {
  const isHybrid = isFulfillable && isWarehouse;
  const inventories: { item_id: string; variants: SaveVariant[] }[] = [];

  for (const itemId of Object.keys(loose)) {
    const item = data.find(x => x.item_id === itemId);
    if (!item) continue;

    const variants: SaveVariant[] = [];

    if ((isManual && isFulfillable) || isHybrid || hasUnaliasedVariant(item)) {
      variants.push({ available: Math.round(loose[itemId].newValue) });
    }

    if (aliasedVariants(item).length < 2) {
      const { attribute } = latestVariant(item);
      if (attribute.alias && attribute.units_per_carton) {
        variants.push({
          available:
            Math.round(
              (cartons[itemId]?.available ?? 0) * attribute.units_per_carton,
            ) || 0,
          alias: attribute.alias,
        });
      }
    } else {
      for (const variant of item.variants) {
        if (variant.alias && variant.attribute?.units_per_carton) {
          variants.push({
            available:
              Math.round(
                (cartons[itemId]?.byVariant[variant._id] ?? 0) *
                  variant.attribute.units_per_carton,
              ) || 0,
            alias: variant.alias,
          });
        }
      }
    }

    inventories.push({ item_id: itemId, variants });
  }

  return { inventories };
}

/** Removes one alias's stock; `resetAlias` also clears its carton set-up. */
export function buildAliasRemoval(
  itemId: string,
  alias: string,
  resetAlias: boolean,
) {
  return {
    inventories: [
      {
        item_id: itemId,
        variants: [{ alias, resetAlias, available: 0 }],
      },
    ],
  };
}

/** Which columns the table has, in order, for this kind of provider. */
export type InventoryColumn =
  | 'product'
  | 'unfulfillable'
  | 'outbound'
  | 'inbound'
  | 'cartons'
  | 'loose'
  | 'reserved'
  | 'carton'
  | 'actions';

export function inventoryColumns({
  isFBA,
  isDeliverr,
  isWarehouse,
  isFulfillable,
  showLooseColumn,
}: InventoryFlags & { showLooseColumn: boolean }): InventoryColumn[] {
  return [
    'product',
    'unfulfillable',
    ...(isWarehouse ? (['outbound'] as const) : []),
    'inbound',
    'cartons',
    ...(showLooseColumn ? (['loose'] as const) : []),
    ...(isFulfillable && !isWarehouse ? (['reserved'] as const) : []),
    ...(!isFBA && !isDeliverr && isWarehouse ? (['carton'] as const) : []),
    'actions',
  ];
}

/** Units held under no alias get their own column. */
export function showsLooseColumn(
  { isFBA, isManual, isWarehouse, isFulfillable }: InventoryFlags,
  hasLoose: boolean,
) {
  if (isFulfillable && !isWarehouse) return false;

  return (
    (isManual && isFulfillable) ||
    (!isFBA && hasLoose) ||
    (isFulfillable && isWarehouse)
  );
}

/** A provider counted in units heads its first quantity column so. */
export function cartonsColumnHeader(
  { isFBA, isDeliverr, isManual, isWarehouse, isFulfillable }: InventoryFlags,
  showLooseColumn: boolean,
): { label: string; inUnits: boolean } {
  const isHybrid = isFulfillable && isWarehouse;
  const inUnits =
    (isFBA || isDeliverr || (isManual && !isHybrid) || isFulfillable) &&
    !showLooseColumn;

  return {
    label:
      inUnits && isFulfillable && !isWarehouse
        ? 'Loose Qty'
        : 'Available Cartons',
    inUnits,
  };
}

/** What one item's quantity cells hold. */
export type QuantityView = {
  /** The loose units are shown, as an input or a figure. */
  showsLoose: boolean;
  /** The cartons are an input (manual and hybrid providers only). */
  showsCartonInput: boolean;
  /** Cartons and loose units are both on show, so their total follows. */
  showsTotal: boolean;
  /** The units on hand, with what is reserved, that the days left count from. */
  onHand: number;
};

export function quantityView(
  item: InventoryItem,
  {
    flags: { isFBA, isManual, isWarehouse, isFulfillable },
    readOnly,
    cartons,
    loose,
  }: {
    flags: InventoryFlags;
    readOnly: boolean;
    cartons?: CartonQuantities[string];
    loose?: LooseQuantities[string];
  },
): QuantityView {
  const latest = latestVariant(item).attribute;
  const aliases = aliasCount(item, latest);
  const isHybrid = isFulfillable && isWarehouse;
  const available = cartons?.available ?? 0;

  const showsLoose =
    (isManual && isFulfillable) ||
    (isManual && !isWarehouse && !isFulfillable) ||
    isHybrid ||
    (!isFBA && !isFulfillable);

  let showsCartonInput = !!latest.alias;
  if (isFulfillable) {
    showsCartonInput = isWarehouse
      ? aliases >= 1
      : !!cartons?.oldValue || aliases > 1;
  }

  const reserved =
    (isFBA ? item.reservedWarehouseTransferQuantity : item.reservedQuantity) ||
    0;
  const looseUnits = Number(loose?.newValue);
  let onHand: number;

  if (readOnly) {
    onHand = (item.available ?? 0) + reserved;
  } else if (hasManyAliases(item)) {
    onHand = looseUnits + available;
  } else {
    onHand = Math.round(
      looseUnits + Math.round(available * perCarton(latest)) + reserved,
    );
  }

  return {
    showsLoose,
    showsCartonInput,
    showsTotal: showsLoose && showsCartonInput,
    onHand,
  };
}

/** "(12 Days Left)" and the like, after the units on hand. */
export type StockOutlook =
  | { kind: 'processing' | 'out' | 'no-velocity' }
  | { kind: 'days'; days: number }
  | { kind: 'long'; span: string };

export function stockOutlook(
  item: InventoryItem,
  onHand: number,
  isProcessing: boolean,
  humanise: (days: number) => string,
): StockOutlook {
  if (isProcessing) return { kind: 'processing' };
  if (!onHand) return { kind: 'out' };

  const days = item.days_left ?? 0;
  if (days < 1) return { kind: 'no-velocity' };
  if (days < 360) return { kind: 'days', days };

  return { kind: 'long', span: humanise(days) };
}

/** Lots of the same alias read as one variant with its lots listed. */
export function mergeLotsByAlias(data: InventoryItem[]): InventoryItem[] {
  return data.map(item => {
    const variants: InventoryVariant[] = [];

    for (const variant of item.variants ?? []) {
      const existing = variants.find(x => x.alias === variant.alias);
      const hasWms = CommonUtility.isValidObject(variant.wms_data);
      const lot = { ...variant.wms_data, available: variant.available };

      if (existing) {
        existing.available += variant.available;
        if (hasWms) existing.lots = [...(existing.lots ?? []), lot];
      } else {
        variants.push({ ...variant, lots: hasWms ? [lot] : [] });
      }
    }

    return { ...item, variants };
  });
}
